import { useEffect, useState } from "react";
import axios from "axios";
import { LogOut, Trash2, Copy, MessageSquareText } from "lucide-react";
import { useLanguage } from "../i18n/LanguageContext";
import { Reveal } from "../components/ui-bits";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
axios.defaults.withCredentials = true;

export default function Admin() {
  const { t } = useLanguage();
  const [user, setUser] = useState(null);
  const [checking, setChecking] = useState(true);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [reservations, setReservations] = useState([]);
  const [confirming, setConfirming] = useState(null);
  const [cancelling, setCancelling] = useState(null);
  const [note, setNote] = useState("");

  const loadReservations = () => axios.get(`${API}/reservations`).then((r) => setReservations(r.data)).catch(() => {});

  useEffect(() => {
    axios.get(`${API}/auth/me`).then((r) => setUser(r.data)).catch(() => {}).finally(() => setChecking(false));
  }, []);

  useEffect(() => {
    if (user) {
      loadReservations();
      loadTemplates();
    }
  }, [user]);

  const login = async (e) => {
    e.preventDefault();
    setError("");
    try {
      const r = await axios.post(`${API}/auth/login`, { email, password });
      setUser(r.data);
    } catch {
      setError(t("admin.loginError"));
    }
  };

  const logout = async () => {
    await axios.post(`${API}/auth/logout`).catch(() => {});
    setUser(null);
  };

  const confirm = async (id) => {
    await axios.post(`${API}/reservations/${id}/confirm`, { message: note || null });
    setConfirming(null);
    setNote("");
    loadReservations();
  };

  const cancel = async (id) => {
    await axios.post(`${API}/reservations/${id}/cancel`, { reason: note || null });
    setCancelling(null);
    setNote("");
    loadReservations();
  };

  const reopen = async (id) => {
    await axios.post(`${API}/reservations/${id}/reopen`);
    loadReservations();
  };

  const [copied, setCopied] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [customTpls, setCustomTpls] = useState([]);
  const [tplName, setTplName] = useState("");
  const [tplText, setTplText] = useState("");

  const del = async (id) => {
    if (deleting !== id) {
      setDeleting(id);
      setTimeout(() => setDeleting(null), 4000);
      return;
    }
    await axios.delete(`${API}/reservations/${id}`);
    setDeleting(null);
    loadReservations();
  };

  const loadTemplates = () => axios.get(`${API}/templates`).then((r) => setCustomTpls(r.data.items || [])).catch(() => {});

  const addTemplate = async () => {
    if (!tplName.trim() || !tplText.trim()) return;
    await axios.post(`${API}/templates`, { name: tplName.trim(), text: tplText.trim() });
    setTplName("");
    setTplText("");
    loadTemplates();
  };

  const deleteTemplate = async (id) => {
    await axios.delete(`${API}/templates/${id}`);
    loadTemplates();
  };

  const copyText = async (key, text) => {
    await navigator.clipboard.writeText(text || t(`admin.${key}`));
    setCopied(key);
    setTimeout(() => setCopied(null), 2000);
  };

  if (checking) return <main className="pt-40 pb-24 text-center text-[#9db8d6]">…</main>;

  if (!user) {
    return (
      <main className="pt-40 pb-24 px-5">
        <form onSubmit={login} className="glass rounded-3xl p-10 max-w-md mx-auto space-y-5" data-testid="admin-login-form">
          <h1 className="font-disco text-3xl neon text-center">{t("admin.title")}</h1>
          <div>
            <label className="block text-xs font-bold uppercase tracking-widest text-[#9db8d6] mb-2">E-mail</label>
            <input type="email" required data-testid="admin-email-input" className="field" value={email} onChange={(e) => setEmail(e.target.value)} />
          </div>
          <div>
            <label className="block text-xs font-bold uppercase tracking-widest text-[#9db8d6] mb-2">{t("admin.password")}</label>
            <input type="password" required data-testid="admin-password-input" className="field" value={password} onChange={(e) => setPassword(e.target.value)} />
          </div>
          {error && <p className="text-sm text-red-400" data-testid="admin-login-error">{error}</p>}
          <button type="submit" data-testid="admin-login-button" className="btn-neon w-full font-display tracking-[0.25em] uppercase px-8 py-3 rounded-full">
            {t("admin.login")}
          </button>
        </form>
      </main>
    );
  }

  return (
    <main className="pt-32 pb-24 px-5">
      <div className="max-w-7xl mx-auto space-y-10">
        <div className="flex items-center justify-between">
          <h1 className="font-disco text-3xl neon">{t("admin.title")}</h1>
          <button onClick={logout} data-testid="admin-logout-button" className="btn-ghost px-5 py-2 rounded-full text-sm inline-flex items-center gap-2">
            <LogOut size={14} /> {t("admin.logout")}
          </button>
        </div>

        <div className="grid lg:grid-cols-3 gap-8 items-start">
        <Reveal delay={0.1} className="lg:col-span-2">
          <section className="glass rounded-3xl p-8" data-testid="admin-reservations">
            <h2 className="font-display text-2xl uppercase tracking-widest neon mb-6">{t("admin.reservations")} ({reservations.length})</h2>
            {reservations.length === 0 ? (
              <p className="text-[#9db8d6] text-sm">{t("admin.noReservations")}</p>
            ) : (
              <div data-testid="admin-reservations-list">
                {reservations.map((r) => (
                  <div key={r.id} className="border-b border-[#00F0FF]/8 py-3">
                    <div className="flex flex-wrap items-center gap-x-5 gap-y-2 justify-between">
                      <div className="flex flex-wrap items-baseline gap-x-5 gap-y-1 text-sm text-[#E2F1FF] min-w-0">
                        <span className="font-semibold">{r.name}</span>
                        <span className="text-[#00F0FF]">{r.type}{r.arrangement ? ` · ${r.arrangement}` : ""}{r.formula ? ` · ${r.formula}` : ""}</span>
                        <span>{r.date} {r.time}</span>
                        <span>{r.people} pers.</span>
                        <span className="text-[#9db8d6] break-all">{r.email} · {r.phone}{r.message ? <span className="italic"> — {r.message}</span> : null}</span>
                      </div>
                      <div className="flex flex-wrap items-center gap-2 shrink-0" data-testid={`reservation-status-${r.id}`}>
                        {r.status === "confirmed" ? (
                          <span className="text-xs font-bold uppercase tracking-widest text-emerald-400 border border-emerald-400/40 bg-emerald-400/10 px-3 py-1 rounded-full">{t("admin.confirmed")}</span>
                        ) : r.status === "cancelled" ? (
                          <>
                            <span className="text-xs font-bold uppercase tracking-widest text-red-400 border border-red-400/40 bg-red-400/10 px-3 py-1 rounded-full">{t("admin.cancelled")}</span>
                            <button
                              data-testid={`reservation-reopen-${r.id}`}
                              onClick={() => reopen(r.id)}
                              className="text-xs font-display tracking-[0.15em] uppercase px-4 py-2 rounded-full border border-[#9db8d6]/40 text-[#9db8d6] hover:text-[#E2F1FF] hover:bg-[#E2F1FF]/5 transition-colors"
                            >
                              {t("admin.reopen")}
                            </button>
                          </>
                        ) : (
                          <>
                            <button
                              data-testid={`reservation-confirm-${r.id}`}
                              onClick={() => { setConfirming(confirming === r.id ? null : r.id); setCancelling(null); setNote(""); }}
                              className="btn-neon text-xs font-display tracking-[0.15em] uppercase px-4 py-2 rounded-full"
                            >
                              {t("admin.confirm")}
                            </button>
                            <button
                              data-testid={`reservation-cancel-${r.id}`}
                              onClick={() => { setCancelling(cancelling === r.id ? null : r.id); setConfirming(null); setNote(""); }}
                              className="text-xs font-display tracking-[0.15em] uppercase px-4 py-2 rounded-full border border-red-400/50 text-red-400 hover:bg-red-400/10 transition-colors"
                            >
                              {t("admin.cancel")}
                            </button>
                          </>
                        )}
                        <button
                          data-testid={`reservation-delete-${r.id}`}
                          onClick={() => del(r.id)}
                          title={t("admin.delete")}
                          className={`p-1.5 rounded-full transition-colors ${deleting === r.id ? "text-red-400 bg-red-400/15" : "text-[#9db8d6] hover:text-red-400"}`}
                        >
                          {deleting === r.id ? <span className="text-xs font-bold px-1">{t("admin.deleteConfirm")}</span> : <Trash2 size={15} />}
                        </button>
                      </div>
                    </div>
                    {confirming === r.id && (
                      <div className="pt-3">
                        <div className="flex flex-wrap gap-2 mb-3 items-center" data-testid={`note-templates-${r.id}`}>
                          <span className="text-[10px] uppercase tracking-widest text-[#9db8d6]">{t("admin.quickFill")}:</span>
                          {[
                            { id: "tpl1", name: t("admin.tpl1Name"), text: t("admin.tpl1") },
                            { id: "tpl2", name: t("admin.tpl2Name"), text: t("admin.tpl2") },
                            ...customTpls,
                          ].map((tpl) => (
                            <button
                              key={tpl.id}
                              data-testid={`note-fill-${r.id}-${tpl.id}`}
                              onClick={() => setNote((n) => (n ? n.trim() + " " : "") + tpl.text)}
                              className="text-[11px] px-3 py-1 rounded-full border border-[#F0C971]/40 text-[#F0C971] hover:bg-[#F0C971]/10 transition-colors"
                            >
                              {tpl.name}
                            </button>
                          ))}
                        </div>
                        <div className="flex flex-col sm:flex-row gap-3 items-stretch sm:items-center">
                          <input
                            data-testid={`reservation-note-${r.id}`}
                            className="field flex-1"
                            placeholder={t("admin.notePlaceholder")}
                            value={note}
                            onChange={(e) => setNote(e.target.value)}
                          />
                          <button
                            data-testid={`reservation-send-confirm-${r.id}`}
                            onClick={() => confirm(r.id)}
                            className="btn-neon font-display text-xs tracking-[0.2em] uppercase px-6 py-2.5 rounded-full whitespace-nowrap"
                          >
                            {t("admin.confirmSend")}
                          </button>
                        </div>
                      </div>
                    )}
                    {cancelling === r.id && (
                      <div className="pt-3">
                        <div className="flex flex-wrap gap-2 mb-3 items-center" data-testid={`note-templates-cancel-${r.id}`}>
                          <span className="text-[10px] uppercase tracking-widest text-[#9db8d6]">{t("admin.quickFill")}:</span>
                          {[
                            { id: "tpl2", name: t("admin.tpl2Name"), text: t("admin.tpl2") },
                            ...customTpls,
                          ].map((tpl) => (
                            <button
                              key={tpl.id}
                              data-testid={`note-fill-cancel-${r.id}-${tpl.id}`}
                              onClick={() => setNote((n) => (n ? n.trim() + " " : "") + tpl.text)}
                              className="text-[11px] px-3 py-1 rounded-full border border-[#F0C971]/40 text-[#F0C971] hover:bg-[#F0C971]/10 transition-colors"
                            >
                              {tpl.name}
                            </button>
                          ))}
                        </div>
                        <div className="flex flex-col sm:flex-row gap-3 items-stretch sm:items-center">
                          <input
                            data-testid={`reservation-reason-${r.id}`}
                            className="field flex-1"
                            placeholder={t("admin.reasonPlaceholder")}
                            value={note}
                            onChange={(e) => setNote(e.target.value)}
                          />
                          <button
                            data-testid={`reservation-send-cancel-${r.id}`}
                            onClick={() => cancel(r.id)}
                            className="font-display text-xs tracking-[0.2em] uppercase px-6 py-2.5 rounded-full whitespace-nowrap bg-red-500 text-white hover:bg-red-400 transition-colors"
                          >
                            {t("admin.cancelSend")}
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </section>
        </Reveal>

        <Reveal delay={0.12}>
          <section className="glass rounded-3xl p-6 lg:sticky lg:top-28" data-testid="admin-templates">
            <h2 className="font-display text-xl uppercase tracking-widest neon flex items-center gap-2">
              <MessageSquareText size={18} /> {t("admin.templates")}
            </h2>
            <p className="text-xs text-[#9db8d6] mt-1 mb-5">{t("admin.templatesHint")}</p>
            <div className="space-y-4">
              {["tpl1", "tpl2"].map((k) => (
                <div key={k} className="rounded-xl border border-[#00F0FF]/15 bg-[#05070B]/60 p-4" data-testid={`template-${k}`}>
                  <div className="flex items-center justify-between gap-2 mb-2">
                    <span className="font-display text-sm tracking-[0.2em] uppercase text-[#F0C971]">{t(`admin.${k}Name`)}</span>
                    <button
                      data-testid={`template-copy-${k}`}
                      onClick={() => copyText(k)}
                      className="btn-ghost text-xs font-display tracking-[0.15em] uppercase px-3 py-1.5 rounded-full inline-flex items-center gap-1.5"
                    >
                      <Copy size={12} /> {copied === k ? t("admin.copied") : t("admin.copy")}
                    </button>
                  </div>
                  <p className="text-xs text-[#9db8d6] leading-relaxed">{t(`admin.${k}`)}</p>
                </div>
              ))}
            </div>

            {customTpls.length > 0 && (
              <div className="space-y-4 mt-4" data-testid="custom-templates">
                <p className="font-display text-xs tracking-[0.3em] uppercase text-[#9db8d6] pt-2">{t("admin.customTitle")}</p>
                {customTpls.map((tpl) => (
                  <div key={tpl.id} className="rounded-xl border border-[#F0C971]/25 bg-[#05070B]/60 p-4" data-testid={`template-custom-${tpl.id}`}>
                    <div className="flex items-center justify-between gap-2 mb-2">
                      <span className="font-display text-sm tracking-[0.2em] uppercase text-[#F0C971]">{tpl.name}</span>
                      <div className="flex gap-2 items-center">
                        <button
                          data-testid={`template-copy-${tpl.id}`}
                          onClick={() => copyText(tpl.id, tpl.text)}
                          className="btn-ghost text-xs font-display tracking-[0.15em] uppercase px-3 py-1.5 rounded-full inline-flex items-center gap-1.5"
                        >
                          <Copy size={12} /> {copied === tpl.id ? t("admin.copied") : t("admin.copy")}
                        </button>
                        <button
                          data-testid={`template-delete-${tpl.id}`}
                          onClick={() => deleteTemplate(tpl.id)}
                          title={t("admin.delete")}
                          className="p-1.5 rounded-full text-[#9db8d6] hover:text-red-400 transition-colors"
                        >
                          <Trash2 size={13} />
                        </button>
                      </div>
                    </div>
                    <p className="text-xs text-[#9db8d6] leading-relaxed">{tpl.text}</p>
                  </div>
                ))}
              </div>
            )}

            <div className="mt-5 pt-5 border-t border-[#00F0FF]/10 space-y-3">
              <input
                data-testid="template-name-input"
                className="field"
                placeholder={t("admin.tplName")}
                value={tplName}
                onChange={(e) => setTplName(e.target.value)}
              />
              <textarea
                rows="3"
                data-testid="template-text-input"
                className="field"
                placeholder={t("admin.tplText")}
                value={tplText}
                onChange={(e) => setTplText(e.target.value)}
              />
              <button
                onClick={addTemplate}
                data-testid="template-add-button"
                className="btn-neon w-full font-display text-sm tracking-[0.2em] uppercase px-6 py-3 rounded-full"
              >
                {t("admin.addTemplate")}
              </button>
            </div>
          </section>
        </Reveal>
        </div>
      </div>
    </main>
  );
}
